import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  TrackByFunction,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormlyModule, FieldType } from '@ngx-formly/core';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { distinctUntilChanged, takeUntil } from 'rxjs';
import { MatCheckbox } from '@angular/material/checkbox';
import { DynamicOptionsService } from './services/dynamic-options.service';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { FormlyErrorComponent } from '../formly-error.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { commitUserValue } from './user-edit.util';

@Component({
  selector: 'app-custom-checkbox',
  template: `
    <div
      class="mb-4"
      [class.hidden]="isFieldHidden()"
      [class.border]="formControl.invalid && formControl.touched"
      [class.border-red-500]="formControl.invalid && formControl.touched"
      [class.rounded]="formControl.invalid && formControl.touched"
      [class.p-2]="formControl.invalid && formControl.touched">
      @if (props.label) {
        <!--
          [innerHTML] zodat admins via form-builder een <a>-tag in het label en
          option-labels kunnen zetten (bv. naar algemene voorwaarden). Angular's
          DomSanitizer strips XSS-content; <a href target>-tags blijven staan.
          .dd-rich-label triggert het link-styling-block onderaan (::ng-deep want
          innerHTML-DOM krijgt geen Angular view-encapsulation-attr).
          (click)="onLabelClick" houdt link-clicks weg van de checkbox-toggle.
        -->
        <label class="w-full font-medium flex mb-2 justify-between">
          <span class="dd-rich-label" (click)="onLabelClick($event)">
            <span [innerHTML]="props.label"></span>{{ props.required ? ' *' : '' }}
          </span>
          <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
          @if (isLoading()) {
            <mat-spinner diameter="24"> </mat-spinner>
          }
        </label>
      }
      @if (availableOptions()?.length > 0) {
        @for (option of availableOptions(); track trackByOption($index, option)) {
          <div>
            <mat-checkbox
              (change)="updateFormControlValue(option)"
              [checked]="isOptionChecked(option)"
              [formlyDataCy]="field"
              [disabled]="props.disabled">
              <span class="dd-rich-label" [innerHTML]="option.label" (click)="onLabelClick($event)"></span>
            </mat-checkbox>
          </div>
        }
      } @else if (isLoading()) {
        <p class="mb-4 text-accent"><i>Laden van options..</i></p>
      } @else {
        <p class="mb-4 text-accent"><i>Geen options beschikbaar</i></p>
      }
    </div>
    @if (field.props.description) {
      <mat-hint class="inline-block mb-2">
        <span class="dd-rich-label" [innerHTML]="field.props.description" (click)="onLabelClick($event)"></span>
      </mat-hint>
    }
    <app-formly-error [field]="field"></app-formly-error>
  `,
  styles: `
    /*
      Link-styling voor anchors die admin via innerHTML in het label, option-label
      of description plaatst (bv. terms&conditions URL). ::ng-deep nodig omdat
      innerHTML-DOM geen Angular view-encapsulation-attr krijgt.

      Design-rationale:
      - text-primary + persistente underline → legal-content affordance, niet
        slechts hover-only zoals reguliere navigatie-links (zichtbaar ook bij
        focus/keyboard-only en in print).
      - decoration-thickness 1 → 2 op hover voor subtiele tactile feedback zonder
        layout-shift (underline-offset 2px houdt afstand stabiel).
      - focus-visible ring met currentColor → toegankelijkheid (keyboard nav),
        past kleur automatisch aan elke link-staat aan.
      - Click-bubbling onderdrukt in onLabelClick zodat link openen de
        checkbox-toggle niet triggert.
    */
    :host ::ng-deep .dd-rich-label a {
      color: rgb(var(--fuse-text-default));
      font-weight: 500;
      text-decoration: underline;
      text-underline-offset: 2px;
      text-decoration-thickness: 1px;
      transition:
        color 150ms ease,
        text-decoration-thickness 150ms ease;
    }
    :host ::ng-deep .dd-rich-label a:hover {
      color: rgb(var(--fuse-primary-700-rgb));
      text-decoration-thickness: 2px;
    }
    :host ::ng-deep .dd-rich-label a:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
      border-radius: 2px;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormlyModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    FormlyModule,
    FormsModule,
    MatCheckbox,
    InfoTooltipComponent,
    FormlyErrorComponent,
    MatProgressSpinner,
    FormlyDataCyDirective,
  ],
})
export class CustomCheckboxComponent extends FieldType implements OnInit, OnDestroy {
  private destroy$ = injectDestroy();
  private dynamicOptionsService = inject(DynamicOptionsService);

  availableOptions = signal<Array<{ label: string; value: any }>>([]);
  linkedData = signal([]);
  private dynamicFilterValue = signal(undefined);
  isFieldHidden = computed(
    () => this.props.hideIfOptionsEmpty && (this.availableOptions().length === 0 || this.dynamicFilterValue() == null)
  );
  optionsBasedOnOtherField = computed(() => {
    return this.field.props?.optionsArray?.some(optionsItem => {
      return optionsItem.filters?.some(optionsFilter => optionsFilter.basedOn === 'dynamic');
    });
  });

  // TrackBy function for better performance
  trackByOption: TrackByFunction<any> = (index: number, option: any) => {
    return option.value?.id || option.value || option.label || index;
  };

  /**
   * Klik op een anchor binnen het label mag NIET het checkbox-toggle-event triggeren —
   * anders opent de link én vinkt de checkbox tegelijk aan/uit (UX-conflict). Stoppen
   * we de propagatie zodra de click-target een `<a>`-element binnen het rich-label is.
   */
  onLabelClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a')) {
      event.stopPropagation();
    }
  }

  // Cleanup function for dynamic options subscriptions
  private cleanup?: () => void;
  loaded = signal(false);
  isLoading = signal(true);

  ngOnInit() {
    // Initialize form control as array if needed
    if (!Array.isArray(this.formControl.value)) {
      this.field.formControl.setValue([]);
    }

    if (!this.optionsBasedOnOtherField()) {
      this.initializeOptions();
    }
    this.initializeDynamicOptions();
    this.setupValueChanges();
  }

  ngOnDestroy() {
    this.cleanup?.();
  }

  private initializeOptions(): void {
    this.dynamicOptionsService.initializeOptions(this.field).subscribe(options => {
      this.availableOptions.set(options);
      this.isLoading.set(false);
      this.loaded.set(true);
    });
  }

  private initializeDynamicOptions(): void {
    if (!this.optionsBasedOnOtherField()) {
      return;
    }

    const { linkedData, cleanup } = this.dynamicOptionsService.initializeDynamicOptions(
      {
        field: this.field,
        onOptionsUpdate: options => {
          this.availableOptions.set(options);
        },
        onFilterValueUpdate: value => {
          this.dynamicFilterValue.set(value);
        },
      },
      this.destroy$
    );

    this.linkedData.set(linkedData);
    this.cleanup = cleanup;
  }

  private setupValueChanges(): void {
    // Handle form control changes
    this.field.formControl.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(() => {
      // Fetch new options when the filter value changes
      if (this.dynamicFilterValue()) {
        this.dynamicOptionsService
          .fetchOptionsForValue(this.dynamicFilterValue(), this.linkedData(), '', this.field)
          .subscribe(options => {
            this.availableOptions.set(options);
          });
      }
      this.validateRequired();
    });
  }

  validateRequired() {
    if (this.props.required) {
      const hasValue = Array.isArray(this.field.formControl.value) && this.field.formControl.value.length > 0;
      this.field.formControl.setErrors(hasValue ? null : { required: true });
    }
  }

  isOptionChecked(option: { label: string; value: any }): boolean {
    if (!Array.isArray(this.field.formControl.value)) {
      return false;
    }

    return this.field.formControl.value.some(selectedValue =>
      selectedValue?.id !== undefined && option.value?.id !== undefined
        ? selectedValue.id === option.value.id
        : selectedValue === option.value
    );
  }

  updateFormControlValue(option: { label: string; value: any }) {
    this.field.formControl.markAsTouched();
    if (!Array.isArray(this.field.formControl.value)) {
      this.field.formControl.setValue([]);
    }

    const currentValues = [...this.field.formControl.value];
    const isCurrentlySelected = this.isOptionChecked(option);

    if (!isCurrentlySelected) {
      currentValues.push(option.value);
    } else {
      const index = currentValues.findIndex(value =>
        value?.id !== undefined && option.value?.id !== undefined
          ? value.id === option.value.id
          : value === option.value
      );
      if (index !== -1) {
        currentValues.splice(index, 1);
      }
    }

    commitUserValue(this.field.formControl, currentValues);
  }
}
